import type { DOMNode, HTMLReactParserOptions } from "html-react-parser";
import { domToReact } from "html-react-parser";

import type { CodexType } from "@sorcery/cms";
import { InternalLink } from "@sorcery/ui";
import { sluggify } from "@sorcery/utils/universal/string";

import { EmbeddedVideo } from "~/components/molecules/html/EmbeddedVideo";
import { PrimerCard } from "~/components/molecules/primer/PrimerCard";
import { codexPath } from "./paths";

export const annotatePrimerMarkup = (markup: string) => {
  // match [[...]] card definitions
  const cardRegExp = /\[\[([^\]]+)\]\]/g;
  // match {{...}} youtube embeds
  const embedRegExp = /\{\{([^\}]+)\}\}/g;
  // replace with custom tags
  const content = markup
    .replaceAll(cardRegExp, (_, capturedText) => {
      return `<card data-slug=${sluggify(capturedText)}>${capturedText}</card>`;
    })
    .replaceAll(embedRegExp, (_, capturedText) => {
      return `<embed data-url=${capturedText} />`;
    });

  return content;
};

export const getPrimerParserOptions = () =>
  ({
    replace: (domNode) => {
      switch (domNode.type) {
        case "text":
          // This ensures all text nodes have a persistent parent node
          // to handle the google translate chrome extension bug
          // https://github.com/facebook/react/issues/11538
          // https://martijnhols.nl/gists/everything-about-google-translate-crashing-react
          return <span>{domToReact([domNode])}</span>;

        case "tag":
          switch (domNode.name) {
            case "card": {
              const slug = domNode.attribs?.["data-slug"];
              if (slug) {
                return (
                  <PrimerCard slug={slug}>{domToReact(domNode.children as DOMNode[])}</PrimerCard>
                );
                // default to normal render if slug is malformed
              }
              return <span>{domToReact([domNode])}</span>;
            }
            case "embed": {
              const url = domNode.attribs["data-url"];
              if (url) {
                return <EmbeddedVideo url={url} />;
                // default to normal render if url is malformed
              }
              return <span>{domToReact([domNode])}</span>;
            }
          }
      }
    },
  }) satisfies HTMLReactParserOptions;

export const annotateCodexMarkup = (
  markup: string,
  codexes: CodexType[],
  field: CodexType["field"],
) => {
  let content = markup;

  codexes.forEach((c) => {
    // codex entrys
    if (c.field === field && !c.finder) {
      const regex = new RegExp(c.title, "gi");
      content = content.replaceAll(regex, (match) => `<codex data-id=${c._id}>${match}</codex>`);

      // implicit codex entries (with finders)
    } else if (c.field === field && c.finder) {
      c.finder.split(",").forEach((f) => {
        const regex = new RegExp(f, "gi");
        content = content.replaceAll(regex, (match) => `<codex data-id=${c._id}>${match}</codex>`);
      });
    }
  });

  return content;
};

export const getCodexParserOptions = () =>
  ({
    replace: (domNode) => {
      switch (domNode.type) {
        case "text":
          // This ensures all text nodes have a persistent parent node
          // to handle the google translate chrome extension bug
          // https://github.com/facebook/react/issues/11538
          // https://martijnhols.nl/gists/everything-about-google-translate-crashing-react
          return <span>{domToReact([domNode])}</span>;

        case "tag":
          switch (domNode.name) {
            case "codex": {
              const id = domNode.attribs?.["data-id"];
              if (id) {
                return (
                  <InternalLink href={codexPath(id)} color="primary">
                    {domToReact(domNode.children as DOMNode[])}
                  </InternalLink>
                );
                // default to normal render if slug is malformed
              }
              return <span>{domToReact([domNode])}</span>;
            }
          }
      }
    },
  }) satisfies HTMLReactParserOptions;
